import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IconSnackBarComponent } from '@core/icon-snack-bar/icon-snack-bar.component';
import { LoadingSnackBarComponent } from '@core/icon-snack-bar/loading-snack-bar/loading-snack-bar.component';
import * as Sentry from '@sentry/angular';

export type TDuration = number | 'infinite';

@Injectable({
	providedIn: 'root',
})
export class IconSnackBarService {
	duration = 6000;

	constructor(public snackBar: MatSnackBar) {}

	// Please don't forget to dismiss MatSnackBar from the component
	// where it's used if duration is set to infinite
	success(message: string, duration?: TDuration) {
		this.snackBar.openFromComponent(IconSnackBarComponent, {
			data: {
				message,
				icon: 'checkmark',
				status: 'success',
			},
			panelClass: ['au-snackbar', 'success'],
			duration: this.checkTimings(duration),
		});
	}

	/**
	 * Snackbar notification which will be displayed until user dismisses it
	 * @param message - message to display
	 * @returns MatSnackBarRef<IconSnackBarComponent>
	 */
	notification(message: string) {
		return this.snackBar.openFromComponent(IconSnackBarComponent, {
			data: {
				message,
				icon: 'performance-mode',
				status: 'success',
			},
			panelClass: ['au-snackbar', 'success'],
			duration: this.checkTimings('infinite'),
		});
	}

	loading(message: string, loading: boolean, duration: TDuration = 'infinite') {
		return this.snackBar.openFromComponent(LoadingSnackBarComponent, {
			data: {
				message,
				loading,
				status: 'success',
			},
			panelClass: ['au-snackbar', 'min-width', 'success'],
			duration: this.checkTimings(duration),
		});
	}

	warning(message: string, duration?: TDuration) {
		this.snackBar.openFromComponent(IconSnackBarComponent, {
			data: {
				message,
				icon: 'exclamation_mark',
				status: 'warning',
			},
			panelClass: ['au-snackbar', 'warning'],
			duration: this.checkTimings(duration),
		});
	}

	error(message: string, duration?: TDuration) {
		if (message) {
			Sentry.captureMessage(`User error snackbar: ${message}`, 'error');
		}

		this.snackBar.openFromComponent(IconSnackBarComponent, {
			data: {
				message,
				icon: 'exclamation_mark',
				status: 'critical',
			},
			panelClass: ['au-snackbar', 'error'],
			duration: this.checkTimings(duration),
		});
	}

	private checkTimings(duration?: TDuration): number {
		let notifyTime;
		if (!duration) {
			notifyTime = this.duration;
		} else {
			notifyTime = duration === 'infinite' ? null : duration;
		}
		return notifyTime;
	}
}
